import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { colors, viz } from "../../styles";
import { SeriesDef } from "../ChartCard";
import ChartTooltip from "../ChartTooltip";

/**
 * Horizontal (part-to-whole / leaderboard) bars, optionally stacked.
 *
 * Direct value labels are on by default. That's the documented relief for the
 * sub-3:1 contrast WARN on two of this dashboard's categorical slots — the
 * number is legible whether or not you can separate the two fills.
 */
const HorizontalBars = <T extends Record<string, any>>({
  data,
  series,
  categoryKey,
  height = 180,
  stacked = true,
  showValueLabels = true,
  categoryWidth = 72,
  valueLabelFormatter,
  valueTickFormatter,
  minLabelFraction = 0.08,
  onBarClick,
}: {
  data: T[];
  series: SeriesDef[];
  categoryKey: string;
  height?: number;
  stacked?: boolean;
  showValueLabels?: boolean;
  /**
   * Width reserved for the category axis. 72px fits a first name; a card whose
   * categories are full creator or business names ("RUN IT UP EVENTS") needs
   * more, or the labels silently lose their leading characters — "PARTIESNYC"
   * rendered as "ARTIESNYC", which is a different, plausible-looking name.
   */
  categoryWidth?: number;
  /** Formats the direct value label — e.g. thousands separators, or currency. */
  valueLabelFormatter?: (value: number) => string;
  /**
   * Formats the VALUE axis ticks. Supply this whenever the underlying numbers
   * are not what they appear to be — the revenue leaderboard's data is in
   * CENTS, so an unformatted axis reading "800000" means $8,000 and invites
   * someone to read it as eight hundred thousand dollars.
   */
  valueTickFormatter?: (value: number) => string;
  /**
   * Hide a stacked segment's own label when the segment is narrower than this
   * fraction of the widest row. Recharts centres a segment label regardless of
   * whether it fits, so on a stacked bar the small segments' labels spill over
   * their neighbours and the outermost one clips off the plot entirely. The
   * exact figures stay in the tooltip and the Table toggle.
   */
  minLabelFraction?: number;
  /** Optional drill-through; receives the row behind the clicked bar. */
  onBarClick?: (row: T) => void;
}) => {
  // The widest row, for the small-segment label threshold above.
  const maxRowTotal = data.reduce((max, row) => {
    const total = series.reduce((sum, item) => sum + (Number(row[item.key]) || 0), 0);
    return Math.max(max, total);
  }, 0);

  return (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart
      data={data}
      layout="vertical"
      // Unstacked bars carry their value label to the RIGHT of the bar, outside
      // the plot area, so the margin has to hold it. 28px fits three digits; a
      // six-digit message count needs ~60, and without it the longest bar — the
      // one the card exists to show — is the one whose number gets clipped.
      margin={{ top: 4, right: stacked ? 28 : 64, bottom: 0, left: 0 }}
      barCategoryGap="28%"
    >
      <CartesianGrid stroke={viz.gridline} horizontal={false} />
      <XAxis
        type="number"
        tick={{ fill: viz.axisInk, fontSize: 11 }}
        tickLine={false}
        axisLine={{ stroke: viz.axis }}
        allowDecimals={false}
        tickFormatter={valueTickFormatter}
      />
      <YAxis
        type="category"
        dataKey={categoryKey}
        tick={{ fill: viz.axisInk, fontSize: 11 }}
        tickLine={false}
        axisLine={false}
        width={categoryWidth}
      />
      <Tooltip cursor={{ fill: colors.surfaceMuted }} content={<ChartTooltip />} />
      {series.map((item, index) => (
        <Bar
          key={item.key}
          dataKey={item.key}
          name={item.label}
          stackId={stacked ? "stack" : undefined}
          fill={item.color}
          maxBarSize={22}
          stroke={colors.surface}
          strokeWidth={2}
          cursor={onBarClick ? "pointer" : undefined}
          // Recharts hands back the row it drew, wrapped in `payload`.
          onClick={
            onBarClick
              ? (entry: any) => entry?.payload && onBarClick(entry.payload as T)
              : undefined
          }
          radius={
            !stacked || index === series.length - 1 ? [0, 4, 4, 0] : undefined
          }
        >
          {showValueLabels && (
            <LabelList
              dataKey={item.key}
              position={stacked ? "center" : "right"}
              // Value labels wear an ink token, never the series color.
              style={{ fill: stacked ? colors.surface : colors.ink, fontSize: 11, fontWeight: 600 }}
              formatter={(value: number) => {
                if (!(value > 0)) return "";
                // A stacked segment too narrow to hold its own label would
                // overprint its neighbours; the tooltip and Table still carry it.
                if (stacked && maxRowTotal > 0 && value / maxRowTotal < minLabelFraction) {
                  return "";
                }
                return valueLabelFormatter ? valueLabelFormatter(value) : value;
              }}
            />
          )}
        </Bar>
      ))}
    </BarChart>
  </ResponsiveContainer>
  );
};

export default HorizontalBars;
